/** @format */
// app.module.ts
import { NgModule, LOCALE_ID } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { NgIdleModule } from "@ng-idle/core";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { HeaderComponent } from "./components/header/header.component";
import { SidebarComponent } from "./components/sidebar/sidebar.component";
import { SettingsComponent } from "./pages/settings/settings.component";
import { AnalyticsComponent } from "./pages/analytics/analytics.component";
import { ReportsComponent } from "./pages/reports/reports.component";
import { HomeComponent } from "./pages/home/home.component";
import { DashboardComponent } from "./pages/dashboard/dashboard.component";
import { AddCompanyComponent } from "./components/settings/add-company/add-company.component";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CompanyComponent } from "./components/settings/company/company.component";
import { SubMenuComponent } from "./components/sub-menu/sub-menu.component";
import { EditCompanyComponent } from "./components/settings/edit-company/edit-company.component";
import { PlantComponent } from "./components/settings/plant/plant.component";
import { EmptyStateComponent } from "./components/empty-state/empty-state.component";
import { AddPlantComponent } from "./components/settings/add-plant/add-plant.component";
import { ModalComponent } from "./components/modal/modal.component";
import { EditPlantComponent } from "./components/settings/edit-plant/edit-plant.component";
import { AddUnitComponent } from "./components/settings/add-unit/add-unit.component";
import { CustomDropdownComponent } from "./components/custom-dropdown/custom-dropdown.component";
import { TokenInterceptorService } from "./services/token-interceptor/token-inteceptor.service";
import { AuthorisatonErrorComponent } from "./pages/authorisaton-error/authorisaton-error.component";
import { SuccessToastComponent } from ".//components/success-toast/success-toast.component";
import { SaveModalComponent } from "./components/save-modal/save-modal.component";
import { DataPointComponent } from "./components/settings/data-point/data-point.component";
import { AddDataPointComponent } from "./components/settings/add-data-point/add-data-point.component";
import { EditDataPointComponent } from "./components/settings/edit-data-point/edit-data-point.component";
//Added import for Configure Notifications Component
import { ConfigureNotificationsComponent } from "./components/settings/configure-notifications/configure-notifications.component";
import { NotificationsConfigComponent } from "./components/notification-config/notifications-config.component";
import { DropdownComponent } from "./components/dropdown/dropdown.component";
import { UnitComponent } from "./components/settings/unit/unit.component";
import { AddNonAdminGroupComponent } from "./components/settings/add-group/add-non-admin-group.component";
import { EditUnitComponent } from "./components/settings/edit-unit/edit-unit.component";
import { GroupsComponent } from "./components/settings/groups/groups.component";
import { AddLogsheetComponent } from "./components/settings/add-logsheet/add-logsheet.component";
import { SearchPipe } from "./pipes/search.pipe";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { CheckedCardComponent } from "./components/checked-card/checked-card.component";
import { ScheduleLogsheetComponent } from "./components/settings/schedule-logsheet/schedule-logsheet.component";
import { CustomSwitchComponent } from "./components/custom-switch/custom-switch.component";
import { CustomDateComponent } from "./components/custom-date/custom-date.component";
import { LogsheetComponent } from "./components/settings/logsheet/logsheet.component";
import { EditLogsheetComponent } from "./components/settings/edit-logsheet/edit-logsheet.component";
import { ModificationWindowComponent } from "./components/settings/modification-window/modification-window.component";
import { AssignLogsheetComponent } from "./components/settings/assign-logsheet/assign-logsheet.component";
import { AddSuperUserComponent } from "./components/settings/add-super-user/add-super-user.component";
import { AddAdminUserComponent } from "./components/settings/add-admin-user/add-admin-user.component";
import { EditAdminUserComponent } from "./components/settings/edit-admin-user/edit-admin-user.component";
import { UserComponent } from "./components/settings/user/user.component";
import { AddNonAdminUserComponent } from "./components/settings/add-non-admin-user/add-non-admin-user.component";
import { EditNonAdminUserComponent } from "./components/settings/edit-non-admin-user/edit-non-admin-user.component";
import { ProfileComponent } from "./components/settings/profile/profile.component";
import { DeactivateModalComponent } from "./components/deactivate-modal/deactivate-modal.component";
import { AuditComponent } from "./components/settings/audit/audit.component";
import {
  DatePipe,
  HashLocationStrategy,
  LocationStrategy,
} from "@angular/common";
import { AuditModalComponent } from "./components/audit-modal/audit-modal.component";
import { LogSheetFormComponent } from "./components/home/log-sheet-form/log-sheet-form.component";

import {
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
} from "@danielmoncada/angular-datetime-picker";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { OverviewComponent } from "./pages/overview/overview.component";
import { UnitLogComponent } from "./components/overview/unit-log/unit-log.component";
import { UnitLogEmptyComponent } from "./components/overview/unit-log-empty/unit-log-empty.component";
import { SpinnerComponent } from "./components/spinner/spinner.component";
import { LoaderInterceptor } from "./interceptors/loader.interceptor";
import { PaginationComponent } from "./components/pagination/pagination.component";
import { CheckedBoxComponent } from "./components/checked-box/checked-box.component";
import { InactivityService } from "./services/inactivity-service/inactivity-service.service";
import { AddBrandingConfigComponent } from "./components/settings/add-branding-config/add-branding-config.component";
import { NotificationRecipientsComponent } from "./components/notifications/notification-recipients/notification-recipients.component";
import { NameSelectorComponent } from "./components/name-selector/name-selector.component";
import { NotificationTypeCardComponent } from "./components/notification-type-card/notification-type-card.component";
import { RecipientSelectorComponent } from "./components/recipient-selector/recipient-selector.component";
import { RecipientCardComponent } from "./components/recipient-card/recipient-card.component";
import { NonAdminHomeComponent } from './pages/non-admin-home/non-admin-home.component';
import { AddTempUserComponent } from './components/settings/add-temp-user/add-temp-user.component';
import { AddTempGroupComponent } from './components/settings/add-temp-group/add-temp-group.component';
import { AssignTempFormComponent } from './components/settings/assign-temp-form/assign-temp-form.component';
import { PublicFormComponent } from './components/public-form/public-form.component';
import { GuardModalComponent } from './components/guard-modal/guard-modal.component';
import { FormModalComponent } from "./components/form-modal/form-modal.component";
import { CloudStorageComponent } from "./components/settings/cloud-storage/cloud-storage.component";
import { IntegrationComponent } from './integration.component/integration.component';
import { WorkflowBuilderComponent } from "./components/settings/workflows/workflow-builder.component";
import { WorkflowIconComponent } from "./components/settings/workflows/workflow-icon.component";
import { WorkflowComponent } from "./components/settings/workflow/workflow.component";
import { WorkflowExceptionsComponent } from "./components/settings/workflow-exceptions/workflow-exceptions.component";
import { RoleRegistryComponent } from "./components/settings/role-registry/role-registry.component";
import { WorkflowRuntimeFormComponent } from "./components/workflow-runtime-form/workflow-runtime-form.component";
import { WorkflowLinkLandingComponent } from "./components/workflow-link-landing/workflow-link-landing.component";

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    SidebarComponent,
    SettingsComponent,
    AnalyticsComponent,
    ReportsComponent,
    HomeComponent,
    DashboardComponent,
    AddCompanyComponent,
    CompanyComponent,
    SubMenuComponent,
    EditCompanyComponent,
    PlantComponent,
    EmptyStateComponent,
    AuthorisatonErrorComponent,
    AddPlantComponent,
    AddPlantComponent,
    UnitComponent,
    ModalComponent,
    EditPlantComponent,
    AddUnitComponent,
    CustomDropdownComponent,
    SuccessToastComponent,
    SaveModalComponent,
    DataPointComponent,
    AddDataPointComponent,
    EditDataPointComponent,
    ConfigureNotificationsComponent,
    NotificationsConfigComponent,
    NameSelectorComponent,
    DropdownComponent,
    AddPlantComponent,
    EditUnitComponent,
    AddNonAdminGroupComponent,
    GroupsComponent,
    AddLogsheetComponent,
    LogsheetComponent,
    SearchPipe,
    CheckedCardComponent,
    EditLogsheetComponent,
    ModificationWindowComponent,
    ScheduleLogsheetComponent,
    CustomSwitchComponent,
    CustomDateComponent,
    EditLogsheetComponent,
    AssignLogsheetComponent,
    AddSuperUserComponent,
    AddAdminUserComponent,
    EditAdminUserComponent,
    AssignLogsheetComponent,
    UserComponent,
    AddNonAdminUserComponent,
    EditNonAdminUserComponent,
    ProfileComponent,
    DeactivateModalComponent,
    AuditComponent,
    AuditModalComponent,
    LogSheetFormComponent,
    OverviewComponent,
    UnitLogComponent,
    UnitLogEmptyComponent,
    SpinnerComponent,
    PaginationComponent,
    CheckedBoxComponent,
    AddBrandingConfigComponent,
    NotificationRecipientsComponent,
    NotificationTypeCardComponent,
    RecipientSelectorComponent,
    RecipientCardComponent,
    NonAdminHomeComponent,
    AddTempUserComponent,
    AddTempGroupComponent,
    AssignTempFormComponent,
    PublicFormComponent,
    GuardModalComponent,
    FormModalComponent,
    CloudStorageComponent,
    IntegrationComponent,
    WorkflowBuilderComponent,
    WorkflowIconComponent,
    WorkflowComponent,
    WorkflowExceptionsComponent,
    RoleRegistryComponent,
    WorkflowRuntimeFormComponent,
    WorkflowLinkLandingComponent,
  ],
  imports: [
    BrowserModule,
    NgIdleModule.forRoot(),
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    DragDropModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true,
    },
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    InactivityService,
    DatePipe,
  ],

  bootstrap: [AppComponent],
})
export class AppModule {}
